/**
 * WhatHappenedSection — the deficiency narrative.
 *
 * Inline editing: click the text to edit, blur (or press Cmd/Ctrl+Enter) to
 * save via PATCH /api/pcap/caps/:id with {deficiency}.
 *
 * AI helper: "Help me write this" → POST /api/pcap/caps/:id/ai/enrich. The
 * suggestion lands in a small preview card with "Use this" / "Discard"
 * buttons. The user is always in control of what gets saved.
 *
 * CFR refs: shown as removable chips. Removing one PATCHes the cap with a new
 * cfr_refs array.
 */

import React, { useEffect, useRef, useState } from 'react';
import SectionCard from './SectionCard';
import AiButton from './AiButton';

const API = process.env.REACT_APP_API_URL;

export default function WhatHappenedSection({ data, onRefresh }) {
  const cap = data.cap;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(cap.deficiency || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [suggestion, setSuggestion] = useState(null); // {deficiency, cfr_refs}

  const [newCfr, setNewCfr] = useState('');
  const [chipBusy, setChipBusy] = useState(false);

  const textareaRef = useRef(null);

  useEffect(() => {
    setDraft(cap.deficiency || '');
  }, [cap.deficiency]);

  useEffect(() => {
    if (editing && textareaRef.current) textareaRef.current.focus();
  }, [editing]);

  const saveDeficiency = async (next) => {
    if ((next || '') === (cap.deficiency || '')) {
      setEditing(false);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`${API}/api/pcap/caps/${cap.id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ deficiency: next || null }),
      });
      const d = await res.json();
      if (!res.ok || !d.success) throw new Error(d.message || 'Could not save');
      setEditing(false);
      onRefresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const askAi = async () => {
    setAiLoading(true);
    setAiError(null);
    setSuggestion(null);
    try {
      const res = await fetch(`${API}/api/pcap/caps/${cap.id}/ai/enrich`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const d = await res.json();
      if (!res.ok || !d.success) throw new Error(d.message || 'AI helper unavailable');
      setSuggestion(d.suggestion || null);
    } catch (err) {
      setAiError(err.message);
    } finally {
      setAiLoading(false);
    }
  };

  const useSuggestion = async () => {
    if (!suggestion) return;
    setSaving(true);
    setError(null);
    try {
      const body = {};
      if (suggestion.deficiency) body.deficiency = suggestion.deficiency;
      if (Array.isArray(suggestion.cfr_refs) && suggestion.cfr_refs.length) {
        // merge, don't overwrite, so we don't clobber things the user already added
        const existing = Array.isArray(cap.cfr_refs) ? cap.cfr_refs : [];
        const merged = Array.from(new Set([...existing, ...suggestion.cfr_refs]));
        body.cfr_refs = merged;
      }
      const res = await fetch(`${API}/api/pcap/caps/${cap.id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const d = await res.json();
      if (!res.ok || !d.success) throw new Error(d.message || 'Could not apply suggestion');
      setSuggestion(null);
      onRefresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const updateCfrRefs = async (next) => {
    setChipBusy(true);
    setError(null);
    try {
      const res = await fetch(`${API}/api/pcap/caps/${cap.id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cfr_refs: next }),
      });
      const d = await res.json();
      if (!res.ok || !d.success) throw new Error(d.message || 'Could not update CFR refs');
      onRefresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setChipBusy(false);
    }
  };

  const cfrRefs = Array.isArray(cap.cfr_refs) ? cap.cfr_refs : [];

  const removeCfr = (ref) => {
    updateCfrRefs(cfrRefs.filter(r => r !== ref));
  };

  const addCfr = (e) => {
    e.preventDefault();
    const v = newCfr.trim();
    if (!v) return;
    if (cfrRefs.includes(v)) {
      setNewCfr('');
      return;
    }
    updateCfrRefs([...cfrRefs, v]);
    setNewCfr('');
  };

  return (
    <SectionCard title="What happened" hint="The deficiency or finding, in plain language.">
      {/* Body — editable */}
      {editing ? (
        <div>
          <textarea
            ref={textareaRef}
            value={draft}
            onChange={e => setDraft(e.target.value)}
            onBlur={() => saveDeficiency(draft.trim())}
            onKeyDown={(e) => {
              if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                saveDeficiency(draft.trim());
              } else if (e.key === 'Escape') {
                setDraft(cap.deficiency || '');
                setEditing(false);
              }
            }}
            rows={5}
            placeholder="Describe the deficiency in plain language."
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10"
          />
          <p className="text-[12px] text-slate-400 mt-1">
            {saving ? 'Saving…' : 'Click outside to save, or press Esc to cancel.'}
          </p>
        </div>
      ) : (
        <div
          role="button"
          tabIndex={0}
          onClick={() => setEditing(true)}
          onKeyDown={(e) => { if (e.key === 'Enter') setEditing(true); }}
          className="cursor-text rounded-md -mx-1 px-1 py-0.5 hover:bg-slate-50"
        >
          {cap.deficiency ? (
            <p className="text-sm text-slate-800 whitespace-pre-wrap">{cap.deficiency}</p>
          ) : (
            <p className="text-sm text-slate-400 italic">
              Click here to describe what happened.
            </p>
          )}
        </div>
      )}

      {error && <p className="text-[12px] text-red-700 mt-1">{error}</p>}

      {/* CFR refs */}
      <div className="mt-4">
        <p className="text-[12px] uppercase tracking-wide font-semibold text-slate-500 mb-2">
          CFR or chapter references
        </p>
        <div className="flex flex-wrap items-center gap-1.5">
          {cfrRefs.length === 0 && (
            <span className="text-[12px] text-slate-400 italic mr-1">None yet.</span>
          )}
          {cfrRefs.map(ref => (
            <span
              key={ref}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 ring-1 ring-slate-200 text-[12px] text-slate-700"
            >
              {ref}
              <button
                type="button"
                onClick={() => removeCfr(ref)}
                disabled={chipBusy}
                className="text-slate-400 hover:text-slate-700 disabled:opacity-50"
                aria-label={`Remove ${ref}`}
              >
                ×
              </button>
            </span>
          ))}
          <form onSubmit={addCfr} className="inline-flex items-center gap-1">
            <input
              value={newCfr}
              onChange={e => setNewCfr(e.target.value)}
              placeholder="e.g. 42 CFR 422.504"
              className="rounded-md border border-slate-200 px-2 py-0.5 text-[12px] focus:outline-none focus:ring-2 focus:ring-slate-900/10"
            />
            <button
              type="submit"
              disabled={chipBusy || !newCfr.trim()}
              className="text-[12px] text-slate-600 hover:text-slate-900 disabled:opacity-50"
            >
              Add
            </button>
          </form>
        </div>
      </div>

      {/* AI helper */}
      <div className="mt-5 pt-4 border-t border-slate-100">
        <div className="flex items-center justify-between">
          <p className="text-[12px] text-slate-500">
            Not sure how to phrase it? Let an assistant draft a first pass.
          </p>
          <AiButton label="Help me write this" onClick={askAi} loading={aiLoading} />
        </div>

        {aiError && <p className="text-[12px] text-red-700 mt-2">{aiError}</p>}

        {suggestion && (
          <div className="mt-3 rounded-lg bg-slate-50 ring-1 ring-slate-200 p-3">
            {suggestion.deficiency && (
              <p className="text-sm text-slate-800 whitespace-pre-wrap">{suggestion.deficiency}</p>
            )}
            {Array.isArray(suggestion.cfr_refs) && suggestion.cfr_refs.length > 0 && (
              <p className="text-[12px] text-slate-500 mt-2">
                Suggested references: {suggestion.cfr_refs.join(', ')}
              </p>
            )}
            <div className="mt-3 flex items-center gap-2">
              <button
                type="button"
                onClick={useSuggestion}
                disabled={saving}
                className="px-2.5 py-1 rounded-md bg-slate-900 text-white text-[12px] font-medium hover:bg-slate-800 disabled:opacity-50"
              >
                {saving ? 'Saving…' : 'Use this'}
              </button>
              <button
                type="button"
                onClick={() => setSuggestion(null)}
                className="px-2.5 py-1 rounded-md text-[12px] text-slate-600 hover:bg-slate-100"
              >
                Discard
              </button>
            </div>
          </div>
        )}
      </div>
    </SectionCard>
  );
}
